import { useQuery } from "@tanstack/react-query";
import { useAtom, useAtomValue } from "jotai";
import { useMemo } from "react";

import {
  isAdminViewAtom,
  orgsAtom,
  selectedAccessRoleIdAtom,
  store,
} from "~/atoms";
import { queryClient } from "~/lib/queryClient";
import type { FeatureShape } from "~/utils/effectiveAccess";

import {
  type CanSee,
  type Lens,
  lensOf,
  localRolesOf,
  membershipOf,
  roleOf,
} from "./membership";
import {
  type AccessAt,
  type AccessPayload,
  accessAtOf,
  featuresQuery,
  meAccessQuery,
} from "./payload";

const NO_PLACE: AccessAt = { org: "", location: "" };

const useCurrentAccessAt = (): AccessAt | undefined => {
  const orgs = useAtomValue(orgsAtom);
  const currentOrg = orgs?.find((org) => org?.isCurrent);
  return useMemo(
    () => (currentOrg ? accessAtOf(currentOrg) : undefined),
    [currentOrg],
  );
};

/**
 * The access payload for the current org and location, and the one membership
 * test every nav item, tab and section asks.
 */
export const useAccess = () => {
  const at = useCurrentAccessAt();
  const featuresResult = useQuery({ ...featuresQuery(), enabled: !!at });
  const accessResult = useQuery({
    ...meAccessQuery(at ?? NO_PLACE),
    enabled: !!at,
  });
  const isAdminView = useAtomValue(isAdminViewAtom);
  const [selectedRoleId, setSelectedRoleId] = useAtom(selectedAccessRoleIdAtom);

  const payload: AccessPayload | undefined = accessResult.data;
  const features: FeatureShape[] | undefined = featuresResult.data;
  const role = payload ? roleOf(payload, selectedRoleId) : undefined;
  const lens: Lens = payload
    ? lensOf(payload.lens, isAdminView)
    : isAdminView
      ? "admin"
      : "supplier";

  const canSee: CanSee = useMemo(
    () => membershipOf(role, lens, features ?? []),
    [role, lens, features],
  );

  return {
    hasPlace: !!at,
    isReady: !!payload && !!features,
    error: accessResult.error ?? featuresResult.error,
    refetch: () => {
      void featuresResult.refetch();
      void accessResult.refetch();
    },
    payload,
    features: features ?? [],
    role,
    roles: payload ? localRolesOf(payload) : [],
    lens,
    canSee,
    selectRole: (roleId: string) => setSelectedRoleId(roleId),
    scope: payload?.scope ?? {},
    scopeEmpty: payload?.scopeEmpty ?? {},
  };
};

/**
 * The membership test from whatever the query cache holds right now, for code
 * that runs outside React (the route guard). Undefined until both reads land.
 */
export const cachedCanSee = (): CanSee | undefined => {
  const currentOrg = store.get(orgsAtom)?.find((org) => org?.isCurrent);
  if (!currentOrg) return undefined;
  const payload = queryClient.getQueryData(
    meAccessQuery(accessAtOf(currentOrg)).queryKey,
  );
  const features = queryClient.getQueryData(featuresQuery().queryKey);
  if (!payload || !features) return undefined;
  const role = roleOf(payload, store.get(selectedAccessRoleIdAtom));
  const lens = lensOf(payload.lens, store.get(isAdminViewAtom));
  return membershipOf(role, lens, features);
};
